import {
  Box,
  IconButton,
  Stack,
  TablePagination,
  Typography,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import { PINYWORLD_API_END_POINT } from "constant";
import { getEllipsisTxt } from "helpers/formatters";
import { getChainIdByNetwork } from "helpers/networks";
import { networkConfigs } from "helpers/networks";
import { getNetworkLogo } from "helpers/networks";
import { useEffect } from "react";
import { useCallback } from "react";
import { useState } from "react";
import { useMoralis } from "react-moralis";
import { useHistory } from "react-router-dom";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import GroupIcon from "@mui/icons-material/Group";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import AppsIcon from "@mui/icons-material/Apps";

export default function ExploreMarkerPinHistory() {
  const history = useHistory();

  const { account } = useMoralis();

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [pinnedMarkers, setPinnedMarkers] = useState();
  const [currentPage, setCurrentPage] = useState(0);
  const [fetchingMarkers, setFetchingMarkers] = useState(false);
  const [totalMarkerCount, setTotalMarkerCount] = useState(0);

  const fetchMarkers = useCallback(async () => {
    try {
      setFetchingMarkers(true);

      const response = await axios.post(
        PINYWORLD_API_END_POINT + "marker/query/criteria",
        {
          criteria: {
            pageSize: 10,
            pageNumber: currentPage,
            sortRequestList: [
              { sortField: "createTime", sortDirection: "DESC" },
            ],
          },
        }
      );

      setPinnedMarkers(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setFetchingMarkers(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchMarkers();
  }, [fetchMarkers]);

  const onCurrentPageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await axios.post(
          PINYWORLD_API_END_POINT + "marker/query/criteria/count",
          {
            criteria: {},
          }
        );

        setTotalMarkerCount(response.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchInitialData();
  }, []);

  return (
    <Stack
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        marginBottom: " 16px",
        overflow: "hidden",
      }}
    >
      <Box
        padding="16px"
        sx={{ borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Typography variant="h5">Pin History</Typography>
      </Box>
      <Stack padding="0px 8px">
        {fetchingMarkers || !pinnedMarkers || !pinnedMarkers.length ? (
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: "calc(100% - 32px)",
              borderBottom: "1px solid",
              borderColor: "divider",
              padding: "16px",
              height: "130px",
            }}
          >
            {!fetchingMarkers ? (
              <Typography>No marker found</Typography>
            ) : (
              <Typography>Loading...</Typography>
            )}
          </Stack>
        ) : (
          pinnedMarkers.map((marker) => {
            return (
              <Stack
                flexDirection="row"
                sx={{
                  width: "calc(100%-16px)",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  padding: "16px 8px",
                }}
                key={marker.id}
              >
                <Stack
                  flexDirection={isSmall ? "column" : "row"}
                  alignItems={isSmall ? "" : "center"}
                  sx={{ width: "100%" }}
                >
                  <Stack
                    minWidth={isSmall ? "100%" : "96px"}
                    alignItems="center"
                  >
                    <img
                      src={`https://gateway.moralisipfs.com/ipfs/${marker.imageUrl}`}
                      alt={marker.id}
                      style={{
                        maxWidth: "96px",
                        maxHeight: "96px",
                        minWidth: "96px",
                      }}
                    />
                  </Stack>
                  <Stack
                    sx={{
                      minWidth: isSmall ? "100%" : "360px",
                      marginLeft: isSmall ? "" : "24px",
                      marginTop: isSmall ? "16px" : "",
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      textAlign={isSmall ? "center" : ""}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {marker.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: "8px",
                        minHeight: "40px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        display: "-webkit-box",
                        "-webkit-line-clamp": "2",
                        "-webkit-box-orient": "vertical",
                      }}
                    >
                      {marker.message}
                    </Typography>
                    <Stack
                      flexDirection="row"
                      alignItems="center"
                      sx={{ marginTop: "16px" }}
                    >
                      <img
                        src={getNetworkLogo(marker.network)}
                        alt={marker.network}
                        style={{
                          maxWidth: "24px",
                          maxHeight: "24px",
                          borderRadius: "50%",
                        }}
                        title={marker.network}
                      />

                      <Typography
                        variant="subtitle2"
                        sx={{ marginLeft: "8px", opacity: "0.65" }}
                      >
                        {marker.owner === account
                          ? "owned by you"
                          : getEllipsisTxt(marker.owner, 6)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    sx={{
                      marginTop: isSmall ? "16px" : "",
                      marginLeft: isSmall ? "0px" : "16px",
                      minWidth: "100%",
                    }}
                  >
                    <Stack
                      sx={{
                        width: "100%",
                        maxWidth: isSmall ? "100%" : "280px",
                      }}
                    >
                      <Stack
                        flexDirection="row"
                        alignItems="center"
                        width="128px"
                      >
                        <PinDropOutlinedIcon
                          sx={{
                            color: "gray",
                            width: "18px",
                            height: "18px",
                            marginTop: "-2px",
                          }}
                        />

                        <Typography
                          sx={{ marginLeft: "2px", opacity: "0.65" }}
                          variant="subtitle2"
                        >
                          {marker.minter === account
                            ? "by you"
                            : getEllipsisTxt(marker.minter, 6)}
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" mt="8px">
                        <CalendarMonthIcon
                          sx={{
                            color: "gray",
                            width: "18px",
                            height: "18px",
                            marginTop: "-2px",
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ marginLeft: "2px", opacity: "0.65" }}
                        >
                          {new Date(marker.createTime).toLocaleDateString()}
                        </Typography>
                      </Stack>

                      {marker.eventName ? (
                        <Stack
                          flexDirection="row"
                          width="100%"
                          alignItems="center"
                          mt="8px"
                        >
                          <GroupIcon
                            sx={{
                              color: "gray",
                              width: "18px",
                              height: "18px",
                              marginTop: "-2px",
                            }}
                          />

                          <Typography
                            variant="subtitle2"
                            sx={{
                              opacity: "0.65",
                              marginLeft: "2px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {marker.eventName}
                          </Typography>
                        </Stack>
                      ) : null}

                      {marker.collectionName ? (
                        <Stack
                          flexDirection="row"
                          width="100%"
                          alignItems="center"
                          mt="8px"
                        >
                          <AppsIcon
                            sx={{
                              color: "gray",
                              width: "18px",
                              height: "18px",
                              marginTop: "-2px",
                            }}
                          />

                          <Typography
                            variant="subtitle2"
                            sx={{
                              marginLeft: "2px",
                              opacity: "0.65",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {marker.collectionName}
                          </Typography>
                        </Stack>
                      ) : null}

                      {marker.listing ? (
                        <Stack
                          flexDirection="row"
                          width="100%"
                          alignItems="center"
                          mt="8px"
                        >
                          <SellOutlinedIcon
                            sx={{
                              color: "gray",
                              width: "18px",
                              height: "18px",
                              marginTop: "-2px",
                            }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{ marginLeft: "2px", opacity: "0.65" }}
                          >
                            {`${marker.listing.price.value} ${marker.listing.price.metadata.symbol}`}
                          </Typography>
                        </Stack>
                      ) : null}
                    </Stack>
                    <IconButton
                      onClick={() =>
                        history.push(
                          `/marker/${
                            networkConfigs[getChainIdByNetwork(marker.network)]
                              .code
                          }/${marker.tokenId}`
                        )
                      }
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            );
          })
        )}
        <TablePagination
          component="div"
          count={totalMarkerCount}
          rowsPerPage={10}
          page={currentPage}
          onPageChange={onCurrentPageChange}
          rowsPerPageOptions={[10]}
        />
      </Stack>
    </Stack>
  );
}
